import { useState, useEffect, useCallback, useMemo } from "react";
import { getSyncLog, queryBackend, BACKEND_URL } from "@/services/backendSync";
import {
  getCoreServicesConfig,
  setCoreServicesConfig,
  resetCoreServicesConfig,
  fetchCoreServicesConfigFromFirestore,
  saveCoreServicesConfigToFirestore,
  resetCoreServicesConfigInFirestore,
  pingCoreServices,
  getCoreProxyUrl,
  CORE_ENVIRONMENTS,
  getWhatsAppChatUrl,
} from "@/services/coreServicesClient";
import { useAuth } from "@/contexts/AuthContext";
import {
  Search, RefreshCw, Loader2, CheckCircle2, AlertCircle,
  Clock, Database, Send, ChevronDown, ChevronUp, Trash2,
  BookOpen, ExternalLink, Copy, CheckCheck,
  Server, Eye, EyeOff, ShieldCheck, Zap, Save, Activity, RotateCcw,
  AlertTriangle, ShieldAlert, Globe, ArrowRight, X
} from "lucide-react";

const GATEWAY_DOCS_URL = "https://we360-dev-ep.saluddigital360.com/api/docs";
const GATEWAY_DOCS_USER = "admin";
const GATEWAY_DOCS_PASS = "we360docs2026";

const QUERY_PRESETS = [
  { id: "everything", label: "Todo", endpoint: "/api/patients/everything", desc: "Perfil + observaciones + condiciones" },
  { id: "history", label: "Historial", endpoint: "/api/patients/history", desc: "Historial de formularios" },
  { id: "peso", label: "Peso", endpoint: "/api/patients/metrics?code=29463-7", desc: "LOINC 29463-7" },
  { id: "pa-sys", label: "PA Sist.", endpoint: "/api/patients/metrics?code=8480-6", desc: "LOINC 8480-6" },
  { id: "glucemia", label: "Glucemia", endpoint: "/api/patients/metrics?code=1558-6", desc: "LOINC 1558-6" },
];

function LogEntry({ entry }) {
  const [expanded, setExpanded] = useState(false);
  const ok = entry.ok;
  const ts = new Date(entry.timestamp);
  const timeStr = ts.toLocaleString("es-CO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" });

  return (
    <div
      className="border-b last:border-b-0 cursor-pointer transition-colors hover:bg-gray-50/50"
      style={{ borderColor: "#F1F5F9" }}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-center gap-3 px-4 py-2.5">
        {ok ? <CheckCircle2 size={14} style={{ color: "#16A34A" }} /> : <AlertCircle size={14} style={{ color: "#DC2626" }} />}
        <span
          className="text-xs font-bold px-1.5 py-0.5 rounded"
          style={ok ? { background: "#DCFCE7", color: "#166534" } : { background: "#FEE2E2", color: "#991B1B" }}
        >
          {entry.status || "ERR"}
        </span>
        <span className="text-xs font-semibold" style={{ color: "#1E293B" }}>
          {entry.method} {entry.form}
        </span>
        <span className="text-xs truncate flex-1" style={{ color: "#94A3B8" }}>
          {entry.userId}
        </span>
        <span className="text-[10px] font-mono" style={{ color: "#94A3B8" }}>
          {entry.time}ms
        </span>
        <span className="text-[10px]" style={{ color: "#94A3B8" }}>{timeStr}</span>
        {expanded ? <ChevronUp size={12} style={{ color: "#94A3B8" }} /> : <ChevronDown size={12} style={{ color: "#94A3B8" }} />}
      </div>
      {expanded && (
        <div className="px-4 pb-3">
          <pre
            className="text-[11px] font-mono p-3 rounded-lg overflow-auto"
            style={{ background: "#F8FAFC", color: "#475569", maxHeight: 200 }}
          >
            {JSON.stringify(entry.response || entry.error || "Sin respuesta", null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export default function AdminBackend() {
  const { user } = useAuth();

  const [syncLog, setSyncLog] = useState([]);
  const [email, setEmail] = useState("");
  const [querying, setQuerying] = useState(false);
  const [queryResult, setQueryResult] = useState(null);
  const [activePreset, setActivePreset] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }).catch(() => {});
  };

  const refreshLog = useCallback(() => {
    setSyncLog(getSyncLog());
  }, []);

  useEffect(() => { refreshLog(); }, [refreshLog]);

  const clearLog = () => {
    localStorage.removeItem("we360_sync_log");
    setSyncLog([]);
  };

  const handleQuery = async (preset) => {
    if (!email.trim()) return;
    setActivePreset(preset.id);
    setQuerying(true);
    setQueryResult(null);
    const result = await queryBackend(preset.endpoint, email.trim());
    setQueryResult(result);
    setQuerying(false);
  };

  const okCount = syncLog.filter((e) => e.ok).length;
  const failCount = syncLog.filter((e) => !e.ok).length;

  // ── Core Services Setup & Switcher State ──────────────
  const [coreConfig, setCoreConfig] = useState(() => getCoreServicesConfig());
  const [firestoreConfig, setFirestoreConfig] = useState(null);
  const [loadingFirestore, setLoadingFirestore] = useState(true);

  const [selectedEnv, setSelectedEnv] = useState(coreConfig.environment || "dev");
  const [apiUrl, setApiUrl] = useState(coreConfig.apiUrl || "");
  const [apiKey, setApiKey] = useState(coreConfig.apiKey || "");
  const [botPhone, setBotPhone] = useState(coreConfig.botPhone || "");
  const [forceFallback, setForceFallback] = useState(coreConfig.forceFallback || false);
  const [showKey, setShowKey] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [pingLoading, setPingLoading] = useState(false);
  const [pingResult, setPingResult] = useState(null);
  const [showPingJson, setShowPingJson] = useState(false);

  // ── Double Verification Modal State ──
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmCheckbox, setConfirmCheckbox] = useState(false);
  const [confirmInputText, setConfirmInputText] = useState("");
  const [savingGlobal, setSavingGlobal] = useState(false);

  // ── Reset Modal State ──
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetCheckbox, setResetCheckbox] = useState(false);
  const [resettingGlobal, setResettingGlobal] = useState(false);

  // Cargar configuración en vivo desde Firestore (config/coreServices) al montar
  useEffect(() => {
    let isMounted = true;
    async function loadFirestore() {
      setLoadingFirestore(true);
      try {
        const fsCfg = await fetchCoreServicesConfigFromFirestore();
        if (isMounted) {
          setFirestoreConfig(fsCfg);
          const activeCfg = getCoreServicesConfig();
          if (!activeCfg.hasOverride && fsCfg) {
            setSelectedEnv(fsCfg.environment || "dev");
            setApiUrl(fsCfg.apiUrl || "");
            setApiKey(fsCfg.apiKey || "");
            setBotPhone(fsCfg.botPhone || "");
            setForceFallback(fsCfg.forceFallback || false);
          }
        }
      } catch (err) {
        console.warn("[AdminBackend] Error cargando config de Firestore:", err);
      } finally {
        if (isMounted) setLoadingFirestore(false);
      }
    }
    loadFirestore();
    return () => { isMounted = false; };
  }, []);

  // Sync state if external change event fires
  useEffect(() => {
    const handleConfigChange = (e) => {
      const cfg = e.detail || getCoreServicesConfig();
      setCoreConfig(cfg);
    };
    window.addEventListener("we360_core_config_changed", handleConfigChange);
    return () => window.removeEventListener("we360_core_config_changed", handleConfigChange);
  }, []);

  const handleSelectEnv = (envId) => {
    setSelectedEnv(envId);
    if (CORE_ENVIRONMENTS[envId]) {
      const preset = CORE_ENVIRONMENTS[envId];
      setApiUrl(preset.url || "");
      setApiKey(preset.apiKey || "");
      setBotPhone(preset.botPhone || "");
    }
  };

  // Detectar cambios no guardados respecto a la configuración actualmente activa
  const hasUnsavedChanges = useMemo(() => {
    return (
      selectedEnv !== (coreConfig.environment || "dev") ||
      apiUrl.trim() !== (coreConfig.apiUrl || "").trim() ||
      apiKey.trim() !== (coreConfig.apiKey || "").trim() ||
      botPhone.trim() !== (coreConfig.botPhone || "").trim() ||
      Boolean(forceFallback) !== Boolean(coreConfig.forceFallback)
    );
  }, [selectedEnv, apiUrl, apiKey, botPhone, forceFallback, coreConfig]);

  // Apertura del modal de doble verificación para guardado global en Firestore
  const handleOpenConfirmModal = () => {
    setConfirmCheckbox(false);
    setConfirmInputText("");
    setSaveError(null);
    setShowConfirmModal(true);
  };

  // Confirmación final y escritura segura en Firestore
  const handleConfirmSaveGlobal = async () => {
    if (!confirmCheckbox || confirmInputText.trim().toUpperCase() !== "CONFIRMAR") {
      return;
    }
    setSavingGlobal(true);
    setSaveError(null);
    try {
      const newConfig = {
        environment: selectedEnv,
        apiUrl: apiUrl.trim(),
        apiKey: apiKey.trim(),
        botPhone: botPhone.trim(),
        forceFallback,
      };
      const userEmail = user?.email || "admin@saluddigital360.com";
      const saved = await saveCoreServicesConfigToFirestore(newConfig, userEmail);
      setFirestoreConfig(saved);
      setCoreConfig(getCoreServicesConfig());
      setShowConfirmModal(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err.message || "Error al persistir en Firestore");
    } finally {
      setSavingGlobal(false);
    }
  };

  // Guardado como override local (solo afecta a este navegador)
  const handleSaveLocalOnly = () => {
    const saved = setCoreServicesConfig({
      environment: selectedEnv,
      apiUrl: apiUrl.trim(),
      apiKey: apiKey.trim(),
      botPhone: botPhone.trim(),
      forceFallback,
    });
    setCoreConfig(saved);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2500);
  };

  // Apertura del modal para restablecer a defaults
  const handleOpenResetModal = () => {
    setResetCheckbox(false);
    setSaveError(null);
    setShowResetModal(true);
  };

  // Confirmación final para restablecer defaults globales
  const handleConfirmResetGlobal = async () => {
    if (!resetCheckbox) return;
    setResettingGlobal(true);
    setSaveError(null);
    try {
      const userEmail = user?.email || "admin@saluddigital360.com";
      const reset = await resetCoreServicesConfigInFirestore(userEmail);
      setFirestoreConfig(null);
      setCoreConfig(reset);
      setSelectedEnv(reset.environment);
      setApiUrl(reset.apiUrl);
      setApiKey(reset.apiKey);
      setBotPhone(reset.botPhone);
      setForceFallback(reset.forceFallback);
      setPingResult(null);
      setShowResetModal(false);
    } catch (err) {
      setSaveError(err.message || "Error al restablecer configuración");
    } finally {
      setResettingGlobal(false);
    }
  };

  const handlePingTest = async () => {
    setPingLoading(true);
    setPingResult(null);
    try {
      const res = await pingCoreServices(
        {
          apiUrl,
          apiKey,
          botPhone,
          forceFallback,
        },
        "584124281735"
      );
      setPingResult(res);
    } catch (err) {
      setPingResult({
        ok: false,
        status: 0,
        statusText: "Error de ejecución",
        error: err.message,
        latencyMs: 0,
      });
    } finally {
      setPingLoading(false);
    }
  };

  return (
    <div className="space-y-6">

      {/* WE360 Core Services & WhatsApp Gateway Setup */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm" data-testid="core-services-setup-card">
        {/* Card Header */}
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/70 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#00628F]/10 text-[#00628F] flex items-center justify-center shrink-0">
              <Server size={18} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                WE360 Core Services &amp; WhatsApp Gateway
              </h3>
              <p className="text-xs text-slate-500">
                Setup y Switcher din&aacute;mico de entornos (DEV, STAGING, PROD) en tiempo de ejecuci&oacute;n.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {coreConfig.hasOverride ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200" data-testid="core-override-badge">
                <AlertCircle size={12} /> Override Local Activo
              </span>
            ) : firestoreConfig ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-[#00628F]/10 text-[#00628F] border border-[#00628F]/20" data-testid="core-firestore-badge" title={`Actualizado por: ${firestoreConfig.updatedBy || 'admin'}`}>
                <Globe size={12} /> Global (Firestore Activo)
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="core-standard-badge">
                <CheckCircle2 size={12} /> Defaults (.env)
              </span>
            )}
            {forceFallback && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold bg-purple-100 text-purple-700" data-testid="core-offline-badge">
                Offline Mock
              </span>
            )}
          </div>
        </div>

        {/* Global Firestore Active Info Banner */}
        {firestoreConfig && (
          <div className="px-6 py-2.5 bg-blue-50/70 border-b border-blue-100 flex flex-wrap items-center justify-between gap-2 text-xs text-blue-900" data-testid="core-firestore-info-banner">
            <div className="flex items-center gap-2">
              <Globe size={14} className="text-[#00628F] shrink-0" />
              <span>
                <strong>Configuración Global Activa:</strong> Gestionada vía Firestore en <code className="font-mono text-xs bg-white/80 px-1 py-0.5 rounded border border-blue-200 text-[#00628F]">config/coreServices</code>
              </span>
            </div>
            {firestoreConfig.updatedBy && (
              <div className="text-[11px] text-blue-700">
                Última actualización por: <span className="font-semibold">{firestoreConfig.updatedBy}</span> ({new Date(firestoreConfig.updatedAt).toLocaleString()})
              </div>
            )}
          </div>
        )}

        {/* Unsaved Changes Banner */}
        {hasUnsavedChanges && (
          <div className="px-6 py-2.5 bg-amber-50 border-b border-amber-200 flex flex-wrap items-center justify-between gap-2 text-xs text-amber-900" data-testid="unsaved-changes-banner">
            <div className="flex items-center gap-2">
              <AlertTriangle size={15} className="text-amber-600 shrink-0" />
              <span>
                <strong>Modificaciones pendientes sin guardar:</strong> Los cambios en este formulario no tendrán efecto en el sistema hasta que confirmes el guardado global.
              </span>
            </div>
            <button
              type="button"
              onClick={handleOpenConfirmModal}
              className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-bold text-[11px] transition-colors cursor-pointer shrink-0 shadow-sm"
              data-testid="unsaved-apply-btn"
            >
              Guardar en Firestore
            </button>
          </div>
        )}

        {/* Environment Selector Bar */}
        <div className="p-6 border-b border-slate-100 bg-white">
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
            Seleccionar Ambiente Preconfigurado
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.values(CORE_ENVIRONMENTS).map((env) => {
              const isSelected = selectedEnv === env.id;
              return (
                <button
                  key={env.id}
                  type="button"
                  onClick={() => handleSelectEnv(env.id)}
                  data-testid={`env-btn-${env.id}`}
                  className={`p-3 rounded-xl border text-left transition-all cursor-pointer relative ${
                    isSelected
                      ? "border-[#00628F] bg-[#00628F]/5 shadow-sm ring-2 ring-[#00628F]/20"
                      : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/50"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                      env.badgeColor === "emerald"
                        ? "bg-emerald-100 text-emerald-800"
                        : env.badgeColor === "amber"
                        ? "bg-amber-100 text-amber-800"
                        : env.badgeColor === "blue"
                        ? "bg-sky-100 text-sky-800"
                        : "bg-purple-100 text-purple-800"
                    }`}>
                      {env.badge}
                    </span>
                    {isSelected && (
                      <span className="w-2 h-2 rounded-full bg-[#00628F]" />
                    )}
                  </div>
                  <div className="text-xs font-bold text-slate-800 truncate">{env.name}</div>
                  <div className="text-[10px] text-slate-400 truncate mt-0.5">{env.description}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Form Fields */}
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* API URL */}
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                URL Base del Gateway Core Services
              </label>
              <input
                type="text"
                value={apiUrl}
                onChange={(e) => {
                  setApiUrl(e.target.value);
                  setSelectedEnv("custom");
                }}
                placeholder="https://we360-dev-ep.saluddigital360.com/api"
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#00628F]/20 focus:border-[#00628F]"
                data-testid="core-api-url-input"
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Ruta base para los endpoints <code className="font-mono text-slate-600">/user/daily-dashboard</code>, <code className="font-mono text-slate-600">/user/daily-recommendations</code> y <code className="font-mono text-slate-600">/user/sync-web-user</code>.
              </p>
            </div>

            {/* API Key */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                API Key (Header <code className="font-mono text-slate-500">x-api-key</code>)
              </label>
              <div className="relative">
                <input
                  type={showKey ? "text" : "password"}
                  value={apiKey}
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    setSelectedEnv("custom");
                  }}
                  placeholder="Introduce la x-api-key"
                  className="w-full pl-3.5 pr-20 py-2.5 rounded-xl border border-slate-200 text-xs font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#00628F]/20 focus:border-[#00628F]"
                  data-testid="core-api-key-input"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setShowKey(!showKey)}
                    className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
                    title={showKey ? "Ocultar" : "Mostrar"}
                  >
                    {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(apiKey, "core-key")}
                    className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
                    title="Copiar API Key"
                  >
                    {copiedField === "core-key" ? <CheckCheck size={14} className="text-emerald-600" /> : <Copy size={14} />}
                  </button>
                </div>
              </div>
            </div>

            {/* Bot Phone */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Tel&eacute;fono WhatsApp Bot Asociado
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={botPhone}
                  onChange={(e) => {
                    setBotPhone(e.target.value);
                    setSelectedEnv("custom");
                  }}
                  placeholder="+1 (877) 874-2737 o +1 (555) 178-1374"
                  className="flex-1 px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#00628F]/20 focus:border-[#00628F]"
                  data-testid="core-bot-phone-input"
                />
                {botPhone && (
                  <a
                    href={getWhatsAppChatUrl(botPhone, "Hola Coach WE360")}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-semibold flex items-center gap-1 transition-colors shrink-0"
                    title="Abrir chat en WhatsApp"
                  >
                    <ExternalLink size={13} /> Test Chat
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Resilience Switch: Force Fallback / Offline */}
          <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50/50 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${forceFallback ? "bg-purple-100 text-purple-700" : "bg-emerald-100 text-emerald-700"}`}>
                {forceFallback ? <ShieldCheck size={16} /> : <Zap size={16} />}
              </div>
              <div>
                <div className="text-xs font-bold text-slate-800">
                  {forceFallback ? "Modo Offline Activo (Forzar Fallback)" : "Consumo en Vivo Activo (Proxy Gateway Server-to-Server)"}
                </div>
                <div className="text-[11px] text-slate-500">
                  {forceFallback
                    ? "La aplicación usa datos de muestra instantáneos sin realizar llamadas de red externas."
                    : "Peticiones enrutadas server-to-server vía Cloud Run (/api/core-services/proxy) con bypass de CORS activo."}
                </div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={forceFallback}
                onChange={(e) => setForceFallback(e.target.checked)}
                className="sr-only peer"
                data-testid="core-force-fallback-toggle"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
            </label>
          </div>

          {/* Action Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <div className="flex flex-wrap items-center gap-2">
              {/* Primary Action: Guardar Globalmente en Firestore */}
              <button
                type="button"
                onClick={handleOpenConfirmModal}
                className="px-4 py-2.5 rounded-xl bg-[#00628F] hover:bg-[#004e73] text-white text-xs font-bold flex items-center gap-2 shadow-sm transition-all cursor-pointer"
                data-testid="core-save-global-btn"
              >
                <Globe size={14} />
                Guardar en Firestore (Global)
              </button>

              {/* Secondary Action: Override Local para Pruebas del Desarrollador */}
              <button
                type="button"
                onClick={handleSaveLocalOnly}
                className="px-3.5 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
                title="Guarda solo en el almacenamiento de este navegador, sin modificar Firestore"
                data-testid="core-save-local-btn"
              >
                <Save size={13} />
                Override Local (Solo yo)
              </button>

              {/* Pre-flight Diagnostic Ping */}
              <button
                type="button"
                onClick={handlePingTest}
                disabled={pingLoading}
                className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold flex items-center gap-2 shadow-sm transition-all cursor-pointer disabled:opacity-50"
                data-testid="core-ping-btn"
              >
                {pingLoading ? <Loader2 size={14} className="animate-spin" /> : <Activity size={14} />}
                {pingLoading ? "Probando..." : "Ping Cl\u00ednico (Test en Vivo)"}
              </button>

              {/* Reset Defaults button */}
              {(firestoreConfig || coreConfig.hasOverride) && (
                <button
                  type="button"
                  onClick={handleOpenResetModal}
                  className="px-3.5 py-2.5 rounded-xl border border-red-200 hover:bg-red-50 text-red-600 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
                  data-testid="core-reset-btn"
                >
                  <RotateCcw size={13} /> Restablecer Defaults
                </button>
              )}
            </div>

            {saveSuccess && (
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600 animate-in fade-in">
                <CheckCircle2 size={14} /> ¡Configuración guardada!
              </span>
            )}

            {coreConfig.updatedAt && (
              <span className="text-[10px] text-slate-400">
                Último cambio activo: {new Date(coreConfig.updatedAt).toLocaleTimeString()}
              </span>
            )}
          </div>

          {/* Ping Results Card */}
          {pingResult && (
            <div className={`mt-4 rounded-xl border p-4 transition-all ${
              pingResult.ok
                ? "bg-emerald-50/70 border-emerald-200 text-emerald-900"
                : "bg-red-50/70 border-red-200 text-red-900"
            }`} data-testid="core-ping-result">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 border-b border-current/10">
                <div className="flex items-center gap-2">
                  {pingResult.ok ? (
                    <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                  ) : (
                    <AlertCircle size={16} className="text-red-600 shrink-0" />
                  )}
                  <span className="text-xs font-bold">
                    {pingResult.status === 200 ? "Conexi\u00f3n Exitosa (200 OK)" : `Estado HTTP: ${pingResult.status || "Fallo de Red"}`}
                  </span>
                  <span className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-white/60 border border-current/10">
                    {pingResult.latencyMs} ms
                  </span>
                  {pingResult.isOfflineMode && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-200 text-purple-900">
                      Mock Offline
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setShowPingJson(!showPingJson)}
                  className="text-[11px] font-semibold underline cursor-pointer text-left sm:text-right"
                >
                  {showPingJson ? "Ocultar detalles JSON" : "Ver respuesta JSON"}
                </button>
              </div>

              <div className="pt-2 text-xs space-y-1">
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold">Endpoint Destino:</span>
                  <span className="font-mono text-[11px] truncate">{pingResult.url}</span>
                </div>
                <div className="flex items-center gap-1.5 text-slate-600">
                  <span className="font-semibold">Vía Proxy Gateway:</span>
                  <span className="font-mono text-[11px] truncate">
                    {pingResult.proxyUrl || getCoreProxyUrl("user/daily-dashboard")}
                  </span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 border border-sky-200 shrink-0">
                    Bypass CORS Activo
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold">Protocolo Atwater &amp; Nutrici\u00f3n:</span>
                  <span>
                    {pingResult.isAtwaterValid
                      ? "\u2713 Recibido y validado correctamente"
                      : "\u26a0 Sin payload nutricional o estructura no reconocida"}
                  </span>
                </div>
                {pingResult.error && (
                  <div className="text-red-700 font-mono text-[11px] mt-1">
                    {pingResult.error}
                  </div>
                )}
              </div>

              {showPingJson && (
                <pre className="mt-3 p-3 rounded-lg bg-slate-900 text-slate-100 font-mono text-[11px] overflow-auto max-h-56">
                  {JSON.stringify(pingResult.data || pingResult, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Double Verification Modal for Global Firestore Save */}
      {showConfirmModal && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          data-testid="double-confirm-modal"
        >
          <div className="bg-white rounded-2xl max-w-2xl w-full border border-red-200 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150 my-8">
            {/* Red Critical Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                  <ShieldAlert size={22} className="text-white" />
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded bg-white/20 text-white inline-block mb-0.5">
                    Cambio de Infraestructura Cr&iacute;tico
                  </span>
                  <h3 className="text-base font-bold leading-tight">
                    Modificar Gateway Global de Core Services
                  </h3>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors"
                title="Cerrar"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              {/* Environment Scope Alert */}
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 text-xs space-y-1">
                <div className="flex items-center justify-between text-slate-600">
                  <span>Ambiente Firestore Afectado:</span>
                  <span className="font-mono font-bold text-slate-900 bg-white px-2 py-0.5 rounded border border-slate-200">
                    {process.env.REACT_APP_FIRESTORE_DATABASE_ID === "we360-dev" ? "we360-dev (Ambiente DEV)" : "(default) (Ambiente STAGING / PRODUCCIÓN)"}
                  </span>
                </div>
                <div className="flex items-center justify-between text-slate-600">
                  <span>Usuario Operador:</span>
                  <span className="font-semibold text-slate-800">{user?.email || "itadmin@saluddigital360.com"}</span>
                </div>
                <div className="flex items-center justify-between text-slate-600">
                  <span>Colecci&oacute;n Destino:</span>
                  <span className="font-mono text-slate-700">config / coreServices</span>
                </div>
              </div>

              {/* Global Impact Warning */}
              <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-900 space-y-2">
                <div className="flex items-center gap-2 font-bold text-xs text-red-800">
                  <AlertTriangle size={16} className="text-red-600 shrink-0" />
                  Impacto Global Inmediato en Todo el Sistema:
                </div>
                <ul className="text-xs space-y-1 pl-5 list-disc text-red-800/90">
                  <li>
                    <strong>Todos los usuarios conectados</strong> en este ambiente se actualizar&aacute;n en tiempo real mediante sincronizaci&oacute;n de Firestore.
                  </li>
                  <li>
                    El <strong>Bot de WhatsApp</strong>, las recomendaciones diarias, el Daily Dashboard y la nutrici&oacute;n Atwater apuntar&aacute;n a la nueva URL.
                  </li>
                  <li>
                    Si la URL configurada es inaccesible o el API Key es inv&aacute;lido, el sistema entrar&aacute; en modo contingencia (fallbacks).
                  </li>
                </ul>
              </div>

              {/* Pre-flight Diagnostic Status */}
              <div className="p-3.5 rounded-xl border text-xs">
                {pingResult && pingResult.ok ? (
                  <div className="flex items-start gap-2.5 text-emerald-800 bg-emerald-50/50 p-2 rounded-lg">
                    <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-bold">Pre-flight Test Exitoso:</span> La nueva URL responde correctamente ({pingResult.status} OK, {pingResult.latencyMs} ms). Protocolo Atwater validado.
                    </div>
                  </div>
                ) : pingResult && !pingResult.ok ? (
                  <div className="flex items-start gap-2.5 text-red-800 bg-red-50/50 p-2 rounded-lg">
                    <AlertCircle size={16} className="text-red-600 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-bold">&iexcl;ALERTA DE PRE-FLIGHT FALLIDO!:</span> El &uacute;ltimo ping cl&iacute;nico fall&oacute; ({pingResult.error || `HTTP ${pingResult.status}`}). Aplicar este cambio podr&iacute;a interrumpir el servicio para todos los usuarios.
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2.5 text-amber-800 bg-amber-50/50 p-2 rounded-lg">
                    <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-bold">Pre-flight No Realizado:</span> A&uacute;n no has ejecutado una prueba en vivo contra esta URL. Se recomienda cancelar y hacer clic en <em>"Ping Cl&iacute;nico"</em> antes de guardar.
                    </div>
                  </div>
                )}
              </div>

              {/* Diff Table */}
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="bg-slate-100 px-3.5 py-2 text-[11px] font-bold text-slate-700 uppercase tracking-wider flex items-center justify-between">
                  <span>Comparativa de Cambios</span>
                  <span className="text-[10px] text-slate-500 font-normal">Actual vs Propuesto</span>
                </div>
                <table className="w-full text-xs">
                  <tbody className="divide-y divide-slate-100">
                    <tr className={selectedEnv !== (coreConfig.environment || "dev") ? "bg-amber-50/60 font-semibold" : ""}>
                      <td className="px-3.5 py-2 text-slate-500">Ambiente</td>
                      <td className="px-3.5 py-2 font-mono text-slate-600">{(coreConfig.environment || "dev").toUpperCase()}</td>
                      <td className="px-3.5 py-2 text-center text-slate-400"><ArrowRight size={12} className="inline" /></td>
                      <td className="px-3.5 py-2 font-mono text-[#00628F] font-bold">{selectedEnv.toUpperCase()}</td>
                    </tr>
                    <tr className={apiUrl.trim() !== (coreConfig.apiUrl || "").trim() ? "bg-amber-50/60 font-semibold" : ""}>
                      <td className="px-3.5 py-2 text-slate-500">URL Gateway</td>
                      <td className="px-3.5 py-2 font-mono text-[11px] text-slate-600 truncate max-w-[150px]" title={coreConfig.apiUrl}>{coreConfig.apiUrl}</td>
                      <td className="px-3.5 py-2 text-center text-slate-400"><ArrowRight size={12} className="inline" /></td>
                      <td className="px-3.5 py-2 font-mono text-[11px] text-[#00628F] font-bold truncate max-w-[180px]" title={apiUrl}>{apiUrl}</td>
                    </tr>
                    <tr className={botPhone.trim() !== (coreConfig.botPhone || "").trim() ? "bg-amber-50/60 font-semibold" : ""}>
                      <td className="px-3.5 py-2 text-slate-500">Bot WhatsApp</td>
                      <td className="px-3.5 py-2 font-mono text-slate-600">{coreConfig.botPhone || "Sin Bot"}</td>
                      <td className="px-3.5 py-2 text-center text-slate-400"><ArrowRight size={12} className="inline" /></td>
                      <td className="px-3.5 py-2 font-mono text-[#00628F] font-bold">{botPhone || "Sin Bot"}</td>
                    </tr>
                    <tr className={Boolean(forceFallback) !== Boolean(coreConfig.forceFallback) ? "bg-amber-50/60 font-semibold" : ""}>
                      <td className="px-3.5 py-2 text-slate-500">Modo Offline</td>
                      <td className="px-3.5 py-2 text-slate-600">{coreConfig.forceFallback ? "Offline (Mock)" : "En Vivo"}</td>
                      <td className="px-3.5 py-2 text-center text-slate-400"><ArrowRight size={12} className="inline" /></td>
                      <td className="px-3.5 py-2 font-bold text-[#00628F]">{forceFallback ? "Offline (Mock)" : "En Vivo"}</td>
                    </tr>
                    <tr className={apiKey.trim() !== (coreConfig.apiKey || "").trim() ? "bg-amber-50/60 font-semibold" : ""}>
                      <td className="px-3.5 py-2 text-slate-500">API Key</td>
                      <td className="px-3.5 py-2 font-mono text-[11px] text-slate-600">
                        {coreConfig.apiKey ? `••••••••${coreConfig.apiKey.slice(-4)}` : "Sin Key"}
                      </td>
                      <td className="px-3.5 py-2 text-center text-slate-400"><ArrowRight size={12} className="inline" /></td>
                      <td className="px-3.5 py-2 font-mono text-[11px] text-[#00628F] font-bold">
                        {apiKey ? `••••••••${apiKey.slice(-4)}` : "Sin Key"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Double Verification Gates */}
              <div className="space-y-3 pt-2 border-t border-slate-200">
                {/* Gate 1: Checkbox */}
                <label className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200 hover:bg-slate-100/60 transition-colors cursor-pointer">
                  <input
                    type="checkbox"
                    checked={confirmCheckbox}
                    onChange={(e) => setConfirmCheckbox(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#00628F] focus:ring-[#00628F]"
                    data-testid="confirm-modal-checkbox"
                  />
                  <span className="text-xs text-slate-800 leading-snug">
                    Confirmo que he verificado la URL y credenciales del nuevo Gateway y entiendo el impacto global para todos los usuarios de este ambiente.
                  </span>
                </label>

                {/* Gate 2: Explicit keyword CONFIRMAR */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-700">
                    Para desbloquear el guardado global, escribe la palabra <span className="font-mono text-red-600 font-bold bg-red-50 px-1 py-0.5 rounded">CONFIRMAR</span>:
                  </label>
                  <input
                    type="text"
                    value={confirmInputText}
                    onChange={(e) => setConfirmInputText(e.target.value)}
                    placeholder="Escribe CONFIRMAR aqu&iacute;"
                    className="w-full px-3.5 py-2 rounded-xl border border-slate-300 text-xs font-mono text-slate-900 uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    data-testid="confirm-modal-input"
                  />
                </div>
              </div>

              {saveError && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700 font-medium flex items-center gap-2">
                  <AlertCircle size={14} className="shrink-0" />
                  {saveError}
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                disabled={savingGlobal}
                className="px-4 py-2.5 rounded-xl border border-slate-300 text-slate-700 hover:bg-white text-xs font-semibold transition-colors cursor-pointer"
                data-testid="confirm-modal-cancel-btn"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmSaveGlobal}
                disabled={!confirmCheckbox || confirmInputText.trim().toUpperCase() !== "CONFIRMAR" || savingGlobal}
                className="px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white text-xs font-bold flex items-center gap-2 transition-all cursor-pointer disabled:cursor-not-allowed shadow-sm"
                data-testid="confirm-modal-submit-btn"
              >
                {savingGlobal ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {savingGlobal ? "Guardando en Firestore..." : "Confirmar y Aplicar Globalmente"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          data-testid="reset-confirm-modal"
        >
          <div className="bg-white rounded-2xl max-w-lg w-full border border-slate-200 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <RotateCcw size={18} className="text-amber-400" />
                <h3 className="text-sm font-bold">Restablecer a Valores por Defecto</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowResetModal(false)}
                className="text-white/70 hover:text-white"
                title="Cerrar"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-xs text-slate-600 leading-relaxed">
                Esta acci&oacute;n eliminar&aacute; el documento de configuraci&oacute;n global en Firestore (<code className="font-mono text-slate-800 bg-slate-100 px-1 py-0.5 rounded">config/coreServices</code>) y remover&aacute; cualquier override local en este navegador.
              </p>
              <p className="text-xs text-slate-600 leading-relaxed">
                El sistema volver&aacute; a utilizar los valores configurados en las variables de entorno base (<code className="font-mono text-slate-800 bg-slate-100 px-1 py-0.5 rounded">.env</code>).
              </p>
              <label className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={resetCheckbox}
                  onChange={(e) => setResetCheckbox(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#00628F]"
                  data-testid="reset-modal-checkbox"
                />
                <span className="text-xs text-slate-800">
                  Confirmo que deseo restablecer la configuraci&oacute;n global de Core Services a los defaults del ambiente.
                </span>
              </label>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowResetModal(false)}
                className="px-4 py-2 rounded-xl border border-slate-300 text-slate-700 hover:bg-white text-xs font-semibold"
                data-testid="reset-modal-cancel-btn"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmResetGlobal}
                disabled={!resetCheckbox || resettingGlobal}
                className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer disabled:cursor-not-allowed"
                data-testid="reset-modal-submit-btn"
              >
                {resettingGlobal ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                {resettingGlobal ? "Restableciendo..." : "Restablecer Ahora"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gateway Docs Card */}
      <div className="bg-white rounded-xl overflow-hidden" style={{ border: "1px solid #E2E8F0" }}>
        <div className="flex items-center gap-3 px-5 py-3.5" style={{ borderBottom: "1px solid #F1F5F9", background: "#F8FAFC" }}>
          <BookOpen size={15} style={{ color: "#00628F" }} />
          <h3 className="text-sm font-bold" style={{ color: "#1E293B" }}>Documentación del Gateway (Swagger)</h3>
          <span className="ml-auto flex items-center gap-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full"
            style={{ background: "#DCFCE7", color: "#166534" }}>
            <CheckCircle2 size={10} /> Activo
          </span>
        </div>
        <div className="px-5 py-4">
          <p className="text-xs mb-3" style={{ color: "#64748B" }}>
            Documentación completa de los endpoints del Gateway GCP. Acceso protegido con usuario y contraseña.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <a
              href={GATEWAY_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold"
              style={{ background: "#00628F", color: "white", textDecoration: "none" }}
              data-testid="admin-gateway-docs-link"
            >
              <ExternalLink size={13} /> Ver Swagger UI
            </a>
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs"
              style={{ background: "#F1F5F9", border: "1px solid #E2E8F0" }}>
              <span className="font-semibold" style={{ color: "#94A3B8" }}>Usuario:</span>
              <span className="font-mono font-bold" style={{ color: "#1E293B" }}>{GATEWAY_DOCS_USER}</span>
              <button onClick={() => copyToClipboard(GATEWAY_DOCS_USER, "user")}
                style={{ background: "none", border: "none", cursor: "pointer", color: copiedField === "user" ? "#16A34A" : "#94A3B8" }}>
                {copiedField === "user" ? <CheckCheck size={12} /> : <Copy size={12} />}
              </button>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs"
              style={{ background: "#F1F5F9", border: "1px solid #E2E8F0" }}>
              <span className="font-semibold" style={{ color: "#94A3B8" }}>Password:</span>
              <span className="font-mono font-bold" style={{ color: "#1E293B" }}>{GATEWAY_DOCS_PASS}</span>
              <button onClick={() => copyToClipboard(GATEWAY_DOCS_PASS, "pass")}
                style={{ background: "none", border: "none", cursor: "pointer", color: copiedField === "pass" ? "#16A34A" : "#94A3B8" }}>
                {copiedField === "pass" ? <CheckCheck size={12} /> : <Copy size={12} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Query section */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4" style={{ borderBottom: "1px solid #F1F5F9" }}>
          <div className="flex items-center gap-2 mb-1">
            <Database size={16} style={{ color: "#0284C7" }} />
            <h3 className="text-sm font-bold" style={{ color: "#1E293B" }}>Consultar datos en GCP</h3>
          </div>
          <p className="text-xs" style={{ color: "#94A3B8" }}>
            Consulta los datos almacenados en el backend ({BACKEND_URL.replace("https://", "")})
          </p>
        </div>

        <div className="px-5 py-4">
          {/* Email input */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email del usuario (ej: jose.perez@demo.we360.com)"
                className="w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm"
                style={{ borderColor: "#E2E8F0", color: "#1E293B" }}
                data-testid="backend-email-input"
              />
            </div>
          </div>

          {/* Query presets */}
          <div className="flex flex-wrap gap-2">
            {QUERY_PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => handleQuery(p)}
                disabled={querying || !email.trim()}
                className="px-3 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer"
                style={{
                  background: activePreset === p.id ? "#0284C7" : "#F1F5F9",
                  color: activePreset === p.id ? "white" : "#475569",
                  border: "none",
                  opacity: !email.trim() ? 0.4 : 1,
                }}
                data-testid={`query-${p.id}`}
              >
                {p.label}
                <span className="block text-[9px] font-normal opacity-75">{p.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Query result */}
        {querying && (
          <div className="flex items-center justify-center py-8 border-t" style={{ borderColor: "#F1F5F9" }}>
            <Loader2 size={20} className="animate-spin" style={{ color: "#0284C7" }} />
            <span className="text-xs ml-2" style={{ color: "#94A3B8" }}>Consultando backend...</span>
          </div>
        )}

        {queryResult && !querying && (
          <div className="border-t" style={{ borderColor: "#F1F5F9" }}>
            <div className="flex items-center justify-between px-5 py-2.5" style={{ background: "#F8FAFC" }}>
              <div className="flex items-center gap-2">
                {queryResult.ok ? (
                  <CheckCircle2 size={14} style={{ color: "#16A34A" }} />
                ) : (
                  <AlertCircle size={14} style={{ color: "#DC2626" }} />
                )}
                <span
                  className="text-xs font-bold"
                  style={{ color: queryResult.ok ? "#16A34A" : "#DC2626" }}
                >
                  {queryResult.status} {queryResult.ok ? "OK" : "Error"}
                </span>
                <Clock size={12} style={{ color: "#94A3B8" }} />
                <span className="text-[10px] font-mono" style={{ color: "#94A3B8" }}>{queryResult.time}ms</span>
              </div>
              <span className="text-[10px] font-mono truncate max-w-[400px]" style={{ color: "#94A3B8" }}>
                {queryResult.url?.replace(BACKEND_URL, "")}
              </span>
            </div>
            <pre
              className="px-5 py-4 text-xs font-mono overflow-auto"
              style={{ color: "#475569", maxHeight: 400, background: "white" }}
              data-testid="query-result"
            >
              {queryResult.error
                ? queryResult.error
                : JSON.stringify(queryResult.data, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Sync log */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: "1px solid #F1F5F9" }}>
          <div className="flex items-center gap-2">
            <Send size={16} style={{ color: "#D97706" }} />
            <h3 className="text-sm font-bold" style={{ color: "#1E293B" }}>Log de sincronizaci&oacute;n</h3>
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: "#F1F5F9", color: "#64748B" }}>
              {syncLog.length} registros
            </span>
            {okCount > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#DCFCE7", color: "#166534" }}>
                {okCount} OK
              </span>
            )}
            {failCount > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#FEE2E2", color: "#991B1B" }}>
                {failCount} Error
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refreshLog}
              className="p-1.5 rounded-lg transition-colors hover:bg-gray-100 cursor-pointer"
              style={{ border: "none", background: "none" }}
              data-testid="log-refresh"
            >
              <RefreshCw size={14} style={{ color: "#64748B" }} />
            </button>
            {syncLog.length > 0 && (
              <button
                onClick={clearLog}
                className="p-1.5 rounded-lg transition-colors hover:bg-red-50 cursor-pointer"
                style={{ border: "none", background: "none" }}
                title="Limpiar log"
                data-testid="log-clear"
              >
                <Trash2 size={14} style={{ color: "#94A3B8" }} />
              </button>
            )}
          </div>
        </div>

        {syncLog.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-xs" style={{ color: "#94A3B8" }}>
              Sin registros de sincronizaci&oacute;n. Cuando un formulario se env&iacute;e al backend, aparecer&aacute; aqu&iacute;.
            </p>
          </div>
        ) : (
          <div className="max-h-[400px] overflow-y-auto">
            {syncLog.map((entry, i) => (
              <LogEntry key={i} entry={entry} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
